/**
 * FILE: apps/hub/src/pages/enroll/enrollConstants.ts
 * PURPOSE: Shared enrollment form configuration: constants, helpers, and a configurable Zod form model.
 * AGENT_CONTEXT: Created during ADR candidate c6 (Deepen Hub UI modules). Merged the previously duplicated Indian/International schemas into enrollmentFormModel.
 * LAST_REVIEWED: 2026-06-25
 */

import { z } from 'zod'
import { ATTEMPTS_BY_COURSE, COMBO_TYPES, normaliseCourse, validatePhone } from '@fintree/utils'

export const installmentSchema = z.object({
  amount: z.coerce.number().min(1, 'Amount is required'),
  dueDate: z.string().min(1, 'Due date is required'),
  notes: z.string().optional(),
})

export type InstallmentFormEntry = z.infer<typeof installmentSchema>

export const amountSchema = z.coerce.number().min(1, 'Amount is required')
export const NO_ATTEMPT_NO_PACKAGE_COURSES = ['CDP', 'CHALLENGERS'] as const
export const COURSE_GROUPS = [
  { label: 'CFA', options: ['CFA_L1', 'CFA_L2', 'CFA_L3'] },
  { label: 'FRM', options: ['FRM_P1', 'FRM_P2'] },
  { label: 'Other', options: ['FM'] },
  { label: 'Revision Pack', options: ['REV_PACK', 'REV_CFA_L1', 'REV_CFA_L2', 'REV_CFA_L3'] },
  { label: 'Supplementary', options: ['CDP', 'CHALLENGERS', 'RETAKER', 'JuiceNotes'] },
] as const

export function isNoAttemptNoPackageCourse(course: string | undefined): boolean {
  return NO_ATTEMPT_NO_PACKAGE_COURSES.some((code) => code === course)
}

/** Resolve ATTEMPTS_BY_COURSE map key; prefer normalised code, then raw selection if already canonical. */
export function resolveCourseKeyForAttempts(selectedCourse: string, selectedCombo: string | undefined): string | null {
  if (selectedCourse === 'COMBO') {
    if (!selectedCombo) return null
    return COMBO_TYPES[selectedCombo as keyof typeof COMBO_TYPES]?.startCourse ?? 'CFA_L1'
  }
  if (!selectedCourse) return null
  const fromNormalise = normaliseCourse(selectedCourse) || selectedCourse
  if (fromNormalise in ATTEMPTS_BY_COURSE) return fromNormalise
  if (selectedCourse in ATTEMPTS_BY_COURSE) return selectedCourse
  return null
}

/** Fields common to both Indian and international enrollment forms. */
const baseEnrollmentFields = {
  name: z.string().min(1, 'Name is required'),
  email: z.string().email().optional().or(z.literal('')),
  city: z.string().optional(),
  course: z.string().min(1, 'Course is required'),
  comboType: z.string().optional(),
  revisionMonths: z.number().optional(),
  package: z.string().optional(),
  attempt: z.string().optional(),
  customAttempt: z.string().optional(),
  enrollmentDate: z.string().min(1),
  amount: amountSchema,
  currency: z.enum(['INR', 'USD']),
  paymentMode: z.string().min(1, 'Payment mode is required'),
  razorpayPaymentId: z.string().optional(),
  installments: z.array(installmentSchema),
}

interface EnrollmentRefinementValue {
  course: string
  comboType?: string
  package?: string
  attempt?: string
  customAttempt?: string
}

/** Shared cross-field validation for enrollment forms. */
function applyEnrollmentRefinements(value: EnrollmentRefinementValue, ctx: z.RefinementCtx) {
  if (value.course === 'COMBO' && !value.comboType) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['comboType'], message: 'Combo type is required' })
  }
  const isRevisionCourse = value.course.startsWith('REV_') || value.course === 'REV_PACK'
  const skipPackage = isRevisionCourse || isNoAttemptNoPackageCourse(value.course)
  if (!skipPackage && !value.package) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['package'], message: 'Package is required' })
  }
  if (value.attempt === '__custom__' && !value.customAttempt) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['customAttempt'], message: 'Custom attempt is required' })
  }
}

/** Configurable enrollment form model. Builds the Zod schema for either Indian or international students. */
export const enrollmentFormModel = {
  indian: z
    .object({
      ...baseEnrollmentFields,
      phone: z.string().refine((p) => validatePhone(p), 'Must be a valid 10-digit Indian mobile'),
    })
    .superRefine(applyEnrollmentRefinements),
  international: z
    .object({
      ...baseEnrollmentFields,
      countryCode: z.string().min(2, 'Country code is required'),
      phoneNumber: z.string().min(5, 'Phone number is required'),
      country: z.string().min(1, 'Country is required'),
      state: z.string().optional(),
    })
    .superRefine(applyEnrollmentRefinements),
}

export type IndianFormInput = z.infer<typeof enrollmentFormModel.indian>
export type InternationalFormInput = z.infer<typeof enrollmentFormModel.international>
